import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { X, Send, Loader2, Minimize2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* Animated bowing martial artist SVG — kneeling seiza → bow cycle */
const BowingMascot = ({ size = 40, light = false }) => (
  <svg viewBox="0 0 50 50" style={{ width: size, height: size }} className="bowing-mascot">
    <style>{`
      @keyframes bow {
        0%, 15% {
          /* Kneeling upright */
          transform: rotate(0deg);
        }
        35%, 55% {
          /* Full bow */
          transform: rotate(0deg);
        }
        70%, 85% {
          /* Back upright */
          transform: rotate(0deg);
        }
        100% {
          transform: rotate(0deg);
        }
      }
      @keyframes headBow {
        0%, 15%   { transform: translateY(0) rotate(0deg); }
        35%, 55%  { transform: translateY(6px) rotate(35deg); }
        70%, 85%  { transform: translateY(0) rotate(0deg); }
        100%      { transform: translateY(0) rotate(0deg); }
      }
      @keyframes bodyBow {
        0%, 15%   { transform: rotate(0deg); transform-origin: 25px 32px; }
        35%, 55%  { transform: rotate(30deg); transform-origin: 25px 32px; }
        70%, 85%  { transform: rotate(0deg); transform-origin: 25px 32px; }
        100%      { transform: rotate(0deg); transform-origin: 25px 32px; }
      }
      @keyframes armsBow {
        0%, 15%   { transform: translateY(0) translateX(0); }
        35%, 55%  { transform: translateY(4px) translateX(2px); }
        70%, 85%  { transform: translateY(0) translateX(0); }
        100%      { transform: translateY(0) translateX(0); }
      }
      .bow-head { animation: headBow 3.5s ease-in-out infinite; transform-origin: 25px 18px; }
      .bow-body { animation: bodyBow 3.5s ease-in-out infinite; }
      .bow-arms { animation: armsBow 3.5s ease-in-out infinite; }
    `}</style>
    
    {/* Kneeling legs (static — on the ground) */}
    <ellipse cx="25" cy="44" rx="12" ry="4" fill={light ? "#fff" : "#1e293b"} opacity="0.15"/>
    {/* Left folded leg */}
    <path d="M 16 38 Q 14 42 18 44 L 22 44 Q 24 42 22 38 Z" fill={light ? "#e2e8f0" : "#334155"} stroke={light ? "#fff" : "#1e293b"} strokeWidth="0.8"/>
    {/* Right folded leg */}
    <path d="M 28 38 Q 26 42 28 44 L 32 44 Q 34 42 34 38 Z" fill={light ? "#e2e8f0" : "#334155"} stroke={light ? "#fff" : "#1e293b"} strokeWidth="0.8"/>

    {/* Body group that bows */}
    <g className="bow-body">
      {/* Gi (uniform) torso */}
      <path d="M 18 22 L 17 38 L 33 38 L 32 22 Z" fill={light ? "#f8fafc" : "#f1f5f9"} stroke={light ? "#fff" : "#1e293b"} strokeWidth="0.8"/>
      {/* Gi collar V-shape */}
      <path d="M 22 22 L 25 30 L 28 22" fill="none" stroke={light ? "#cbd5e1" : "#64748b"} strokeWidth="1"/>
      {/* Belt (obi) */}
      <rect x="17" y="31" width="16" height="3" rx="1" fill={light ? "#1e293b" : "#1e293b"} stroke={light ? "#fff" : "#0f172a"} strokeWidth="0.5"/>
      
      {/* Arms that move with bow */}
      <g className="bow-arms">
        {/* Left arm */}
        <path d="M 18 24 Q 12 28 14 34" fill="none" stroke={light ? "#fff" : "#1e293b"} strokeWidth="1.5" strokeLinecap="round"/>
        {/* Left hand */}
        <circle cx="14" cy="34" r="1.5" fill={light ? "#fde68a" : "#fcd34d"}/>
        {/* Right arm */}
        <path d="M 32 24 Q 38 28 36 34" fill="none" stroke={light ? "#fff" : "#1e293b"} strokeWidth="1.5" strokeLinecap="round"/>
        {/* Right hand */}
        <circle cx="36" cy="34" r="1.5" fill={light ? "#fde68a" : "#fcd34d"}/>
      </g>
    </g>

    {/* Head group that bows */}
    <g className="bow-head">
      {/* Head */}
      <circle cx="25" cy="15" r="8" fill={light ? "#fde68a" : "#fcd34d"} stroke={light ? "#fff" : "#1e293b"} strokeWidth="1"/>
      {/* Hair (top-knot / short) */}
      <path d="M 19 11 Q 25 5 31 11" fill={light ? "#475569" : "#1e293b"} stroke="none"/>
      {/* Eyes */}
      <ellipse cx="22" cy="14" rx="1.5" ry="2" fill={light ? "#fff" : "#1e293b"}/>
      <ellipse cx="28" cy="14" rx="1.5" ry="2" fill={light ? "#fff" : "#1e293b"}/>
      {/* Eye highlights */}
      <circle cx="22.5" cy="13.5" r="0.6" fill={light ? "#e2e8f0" : "#fff"}/>
      <circle cx="28.5" cy="13.5" r="0.6" fill={light ? "#e2e8f0" : "#fff"}/>
      {/* Gentle smile */}
      <path d="M 23 18 Q 25 20 27 18" fill="none" stroke={light ? "#fff" : "#1e293b"} strokeWidth="0.8" strokeLinecap="round"/>
    </g>
  </svg>
);

const PublicAIChat = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `pub_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
  const [hasNewMessage, setHasNewMessage] = useState(false);
  const [proactiveShown, setProactiveShown] = useState(false);
  const [proactiveBubble, setProactiveBubble] = useState(null);
  const messagesEndRef = useRef(null);
  const proactiveTimerRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Proactive engagement: show bubble after 8s on key pages
  useEffect(() => {
    if (proactiveShown || isOpen) return;
    const path = location.pathname;
    let msg = null;

    if (path === '/' || path === '') {
      msg = "Looking for martial arts training in Japan? I can help you find the perfect course! 🥋";
    } else if (path.startsWith('/programs') && path.length > 10) {
      msg = "Interested in this course? Ask me anything about it — schedule, skill level, what to expect!";
    } else if (path === '/programs') {
      msg = "Not sure which style is right for you? Tell me your goals and I'll recommend courses!";
    }

    if (msg) {
      proactiveTimerRef.current = setTimeout(() => {
        setProactiveBubble(msg);
        setProactiveShown(true);
      }, 8000);
    }
    return () => clearTimeout(proactiveTimerRef.current);
  }, [location.pathname, proactiveShown, isOpen]);

  // Welcome message when opened
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: 'Welcome to Train In Japan!\n\n**Find your martial art** — Tell me your goals\n**Learn about styles** — Karate, Judo, Kendo & more\n**Plan your trip** — Training in Japan tips\n\nHow can I help? / どのようにお手伝いしましょうか？'
      }]);
    }
  }, [isOpen]);

  const sendMessage = async (text) => {
    const userMsg = (text || input).trim();
    if (!userMsg || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/public/ai/chat`, { message: userMsg, session_id: sessionId });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.reply }]);
      if (isMinimized) setHasNewMessage(true);
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: detail.includes('limit') ? 'Session limit reached. Please refresh to start a new chat!' : 'Sorry, something went wrong. Please try again.'
      }]);
    }
    setLoading(false);
  };

  const openFromProactive = () => {
    setProactiveBubble(null);
    setIsOpen(true);
  };

  const toggleOpen = () => {
    setIsOpen(!isOpen);
    setIsMinimized(false);
    setHasNewMessage(false);
    setProactiveBubble(null);
  };

  // Closed state — mascot button + proactive bubble
  if (!isOpen) {
    return (
      <div className="fixed bottom-6 right-6 z-50">
        {/* Proactive bubble */}
        {proactiveBubble && (
          <div className="absolute bottom-16 right-0 w-64 animate-in slide-in-from-bottom-2 fade-in duration-300">
            <div className="bg-white rounded-2xl shadow-xl border border-emerald-200 p-3 relative">
              <button onClick={() => setProactiveBubble(null)} className="absolute top-1 right-1 text-slate-400 hover:text-slate-600 p-0.5">
                <X className="w-3 h-3" />
              </button>
              <p className="text-sm text-slate-700 pr-4 leading-snug">{proactiveBubble}</p>
              <button onClick={openFromProactive} className="mt-2 text-xs font-medium text-emerald-600 hover:text-emerald-800">
                Chat with me →
              </button>
              <div className="absolute bottom-0 right-6 w-3 h-3 bg-white border-r border-b border-emerald-200 transform rotate-45 translate-y-1.5"></div>
            </div>
          </div>
        )}
        {/* Mascot button — animated bowing martial artist */}
        <button data-testid="public-ai-chat-btn" onClick={toggleOpen} className="group" aria-label="AI Guide">
          <div className="relative">
            <div className="w-14 h-14 bg-gradient-to-br from-emerald-600 to-teal-600 rounded-full flex items-center justify-center shadow-lg shadow-emerald-200 hover:shadow-xl hover:shadow-emerald-300 transition-all duration-300 hover:scale-110 overflow-hidden">
              <BowingMascot size={38} />
            </div>
            {!proactiveBubble && <div className="absolute inset-0 w-14 h-14 bg-emerald-400 rounded-full animate-ping opacity-20"></div>}
          </div>
        </button>
      </div>
    );
  }

  // Open chat window
  return (
    <div data-testid="public-ai-chat-window"
      className={`fixed z-50 transition-all duration-300 ${isMinimized ? 'bottom-6 right-6 w-64' : 'bottom-6 right-6 w-[360px] sm:w-[400px]'}`}>
      <div className={`bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col ${isMinimized ? 'h-auto' : 'h-[520px]'}`}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-emerald-700 to-teal-600 text-white cursor-pointer"
             onClick={() => { if (isMinimized) { setIsMinimized(false); setHasNewMessage(false); } }}>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 flex items-center justify-center">
              <BowingMascot size={28} light />
            </div>
            <div>
              <p className="text-sm font-semibold leading-tight">AI Guide / AIガイド</p>
              <p className="text-xs text-emerald-100">Find your martial art</p>
            </div>
            {hasNewMessage && isMinimized && <span className="w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse"></span>}
          </div>
          <div className="flex items-center gap-1">
            <button onClick={(e) => { e.stopPropagation(); setIsMinimized(!isMinimized); setHasNewMessage(false); }} className="hover:bg-white/20 rounded p-1"><Minimize2 className="w-4 h-4" /></button>
            <button onClick={(e) => { e.stopPropagation(); toggleOpen(); }} className="hover:bg-white/20 rounded p-1"><X className="w-4 h-4" /></button>
          </div>
        </div>

        {!isMinimized && (
          <>
            <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-gradient-to-b from-slate-50 to-white">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                    m.role === 'user' ? 'bg-emerald-600 text-white rounded-br-sm' : 'bg-white text-slate-800 border border-slate-200 rounded-bl-sm shadow-sm'
                  }`}>
                    <div className="whitespace-pre-wrap" dangerouslySetInnerHTML={{
                      __html: m.content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br/>')
                    }} />
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
                    <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {messages.length <= 1 && (
              <div className="px-3 pb-2 flex flex-wrap gap-1.5">
                {[
                  { text: 'Help me choose a martial art', emoji: '🥋' },
                  { text: 'What courses are available?', emoji: '📚' },
                  { text: '初心者におすすめは？', emoji: '🇯🇵' },
                ].map((q, i) => (
                  <button key={i} onClick={() => sendMessage(q.text)}
                    className="text-xs bg-slate-100 hover:bg-emerald-50 border border-slate-200 hover:border-emerald-300 rounded-full px-3 py-1.5 text-slate-600 hover:text-emerald-700 transition-colors">
                    {q.emoji} {q.text}
                  </button>
                ))}
              </div>
            )}

            <div className="p-3 border-t border-slate-100 bg-white">
              <div className="flex gap-2">
                <input data-testid="public-ai-input" value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                  placeholder="Ask anything... / 何でも聞いてください..."
                  className="flex-1 rounded-full border border-slate-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  disabled={loading} />
                <button data-testid="public-ai-send" onClick={() => sendMessage()}
                  disabled={loading || !input.trim()}
                  className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white rounded-full p-2.5 transition-colors">
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PublicAIChat;
